import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAlliance } from '../../types/alliance';
import { LoadingSpinner } from '../LoadingSpinner';
import ProfileAvatar from '../common/ProfileAvatar';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { Button } from '../common/Button';
import { useAllianceStore } from '../../stores/allianceStore';
import { Link } from 'react-router-dom';
import FullBlockingModal from '../common/FullBlockingModal';

const RequestedAlliance = ({
  alliance,
  onCancel,
}: {
  alliance: UserAlliance;
  onCancel: (allianceId: string) => Promise<{ success?: boolean; error?: string }>;
}) => {
  const { t } = useTranslation(['alliances', 'common']);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleCancel = async () => {
    setDeleting(true);
    const result = await onCancel(alliance.alliance_id);
    if (result.error) {
      setDeleteError(result.error);
    }
    setDeleting(false);
    setShowConfirmDialog(false);
  };

  const openConfirmDialog = () => {
    setDeleteError(null);
    setShowConfirmDialog(true);
  };

  return (
    <div key={alliance.alliance_id} className="flex flex-col gap-4 py-4">
      <div className="flex flex-1 flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="mb-2 flex flex-row items-center gap-4 md:mb-0">
          <ProfileAvatar
            profile_id={alliance.user.id}
            first_name={alliance.user.first_name}
            avatar_url={alliance.user.avatar_url}
            className="flex-shrink-0"
          />
          <div className="flex flex-col">
            <Link
              to={`/freelancers/${alliance.user.id}`}
              className="text-sm font-bold text-secondary"
            >
              {alliance.user.first_name}
            </Link>
            <div className="text-sm text-gray-500">{getFreelancerSubtitle(alliance.user, t)}</div>
          </div>
        </div>
        <Button
          onClick={openConfirmDialog}
          disabled={deleting}
          className="w-full md:w-auto md:px-8"
        >
          {deleting
            ? t('common:form_actions.cancelling', { ns: 'common' })
            : t('common:form_actions.cancel', { ns: 'common' })}
        </Button>
      </div>
      {deleteError && <div className="text-red-500">{deleteError}</div>}

      {showConfirmDialog && (
        <ConfirmCancelDialog
          alliance={alliance}
          onConfirm={handleCancel}
          onClose={() => setShowConfirmDialog(false)}
          deleting={deleting}
          error={deleteError}
        />
      )}
    </div>
  );
};

interface ConfirmCancelDialogProps {
  alliance: UserAlliance;
  onConfirm: () => void;
  onClose: () => void;
  deleting: boolean;
  error: string | null;
}

const ConfirmCancelDialog = ({
  alliance,
  onConfirm,
  onClose,
  deleting,
  error,
}: ConfirmCancelDialogProps) => {
  const { t } = useTranslation(['alliances', 'common']);

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <button
        className="absolute right-4 top-4 text-2xl text-gray-400 hover:text-gray-600 md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      <h2 className="mb-4 text-2xl font-bold text-secondary">
        {t('confirmCancelTitle', { ns: 'alliances', defaultValue: "Confirmer l'annulation" })}
      </h2>

      <p className="font-ambit mb-6 text-[0.9375rem] text-secondary">
        {t('confirmCancelMessage', {
          ns: 'alliances',
          name: alliance.user.first_name,
        })}
      </p>

      {error && <div className="mb-4 text-[#F04438]">{error}</div>}

      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        <Button
          className="w-full bg-red-600 text-white md:w-auto md:px-8"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting
            ? t('cancelling', { ns: 'alliances', defaultValue: 'Annulation...' })
            : t('confirmCancelButton', { ns: 'alliances', defaultValue: "Confirmer l'annulation" })}
        </Button>

        <Button
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
          onClick={onClose}
        >
          {t('common:form_actions.cancel')}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

interface RequestedAlliancesProps {
  alliances: UserAlliance[];
  loading: boolean;
}

const RequestedAlliances: React.FC<RequestedAlliancesProps> = ({ alliances, loading }) => {
  const { t } = useTranslation(['alliances', 'common']);
  const { deleteAlliance } = useAllianceStore();
  return (
    <div className="border-2 flex-1 rounded-2xl border-gray-300 bg-white p-4 shadow-xl md:p-6">
      <h2 className="mb-2 text-lg font-semibold text-secondary md:mb-4 md:text-xl">
        {t('myRequests')}
      </h2>

      <div className="mt-4 divide-y divide-[#E1E9F0] rounded-2xl bg-white">
        {alliances.map(alliance => (
          <RequestedAlliance
            key={alliance.alliance_id}
            alliance={alliance}
            onCancel={deleteAlliance}
          />
        ))}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {!loading && alliances.length === 0 && (
          <div className="mb-4 text-sm text-gray-500">{t('noRequests')}</div>
        )}
      </div>
    </div>
  );
};

export default RequestedAlliances;
